import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Bot, User } from 'lucide-react';
import axios from 'axios';

function AgentChat() {
  const [agentUrl, setAgentUrl] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleConnect = () => {
    if (agentUrl.trim()) {
      setIsConnected(true);
      setMessages([
        {
          role: 'system',
          content: `Connected to agent at: ${agentUrl}`,
          timestamp: new Date().toISOString()
        }
      ]);
    }
  };

  const handleDisconnect = () => {
    setIsConnected(false);
    setMessages([]);
    setAgentUrl('');
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Build the query endpoint URL
      let queryUrl = agentUrl;
      
      // Remove trailing slash if present
      if (queryUrl.endsWith('/')) {
        queryUrl = queryUrl.slice(0, -1);
      }
      
      // Add /query if not already present
      if (!queryUrl.endsWith('/query')) {
        queryUrl = `${queryUrl}/query`;
      }
      
      // Add https:// if no protocol specified
      if (!queryUrl.startsWith('http://') && !queryUrl.startsWith('https://')) {
        queryUrl = `https://${queryUrl}`;
      }

      // Send POST request to agent endpoint with correct format
      const response = await axios.post(queryUrl, {
        question: inputMessage  // Changed from 'message' to 'question'
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });

      // Add agent's response to messages
      const agentMessage = {
        role: 'agent',
        content: response.data.answer || response.data.response || response.data.message || JSON.stringify(response.data),
        timestamp: response.data.timestamp || new Date().toISOString(),
        processingTime: response.data.processing_time
      };

      setMessages(prev => [...prev, agentMessage]);
    } catch (error) {
      console.error('Error sending message to agent:', error);
      
      let errorMsg = 'Failed to communicate with agent';
      
      if (error.response?.data?.detail) {
        // Handle validation errors from FastAPI
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg).join(', ');
        } else {
          errorMsg = error.response.data.detail;
        }
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      const errorMessage = {
        role: 'error',
        content: `Error: ${errorMsg}`,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{
      minHeight: 'calc(100vh - 56px)',
      background: '#000000',
      padding: '40px 20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{
        maxWidth: '900px',
        width: '100%',
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 136px)'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'rgba(255, 255, 255, 0.02)'
        }}>
          <h1 style={{
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '600',
            color: '#ffffff'
          }}>
            Agent Chat Interface
          </h1>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.6)'
          }}>
            Connect to any agent endpoint and start chatting
          </p>
        </div>

        {/* Connection Setup */}
        {!isConnected ? (
          <div style={{
            padding: '40px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1
          }}>
            <Bot size={64} style={{ color: '#8b2020', opacity: 0.5 }} />
            
            <div style={{ width: '100%', maxWidth: '500px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255, 255, 255, 0.9)'
              }}>
                Agent URL Endpoint
              </label>
              <input
                type="text"
                value={agentUrl}
                onChange={(e) => setAgentUrl(e.target.value)}
                placeholder="agent1-production-78c0.up.railway.app"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#8b2020';
                  e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleConnect();
                  }
                }}
              />
              <p style={{
                marginTop: '8px',
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.5)',
                fontStyle: 'italic'
              }}>
                Enter base URL only - /query endpoint will be added automatically
              </p>
            </div>

            <button
              onClick={handleConnect}
              disabled={!agentUrl.trim()}
              style={{
                padding: '12px 32px',
                background: agentUrl.trim() ? '#8b2020' : 'rgba(139, 32, 32, 0.3)',
                color: agentUrl.trim() ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: agentUrl.trim() ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (agentUrl.trim()) {
                  e.currentTarget.style.background = '#6b1818';
                }
              }}
              onMouseLeave={(e) => {
                if (agentUrl.trim()) {
                  e.currentTarget.style.background = '#8b2020';
                }
              }}
            >
              Connect to Agent
            </button>
          </div>
        ) : (
          <>
            {/* Chat Messages */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: msg.role === 'user' ? '#8b2020' : 
                                msg.role === 'error' ? '#d32f2f' : 
                                msg.role === 'system' ? 'rgba(255, 255, 255, 0.1)' :
                                'rgba(139, 32, 32, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {msg.role === 'user' ? (
                      <User size={18} style={{ color: '#ffffff' }} />
                    ) : msg.role === 'system' ? (
                      <Bot size={18} style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                    ) : (
                      <Bot size={18} style={{ color: '#ffffff' }} />
                    )}
                  </div>

                  {/* Message Content */}
                  <div style={{
                    maxWidth: '70%',
                    background: msg.role === 'user' ? 'rgba(139, 32, 32, 0.2)' : 
                                msg.role === 'error' ? 'rgba(211, 47, 47, 0.15)' :
                                'rgba(255, 255, 255, 0.05)',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: `1px solid ${
                      msg.role === 'user' ? 'rgba(139, 32, 32, 0.3)' :
                      msg.role === 'error' ? 'rgba(211, 47, 47, 0.3)' :
                      'rgba(255, 255, 255, 0.08)'
                    }`
                  }}>
                    <div style={{
                      color: msg.role === 'error' ? '#ff6b6b' : '#ffffff',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {msg.content}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.4)',
                      marginTop: '6px',
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center'
                    }}>
                      <span>{new Date(msg.timestamp).toLocaleTimeString()}</span>
                      {msg.processingTime && (
                        <span style={{ color: 'rgba(139, 32, 32, 0.8)' }}>
                          • {msg.processingTime.toFixed(2)}s
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Loading Indicator */}
              {isLoading && (
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'rgba(139, 32, 32, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Bot size={18} style={{ color: '#ffffff' }} />
                  </div>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                  }}>
                    <Loader2 size={16} style={{ color: '#8b2020', animation: 'spin 1s linear infinite' }} />
                    <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                      Agent is thinking...
                    </span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div style={{
              padding: '20px 24px',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              background: 'rgba(255, 255, 255, 0.02)'
            }}>
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-end',
                marginBottom: '12px'
              }}>
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message... (Press Enter to send)"
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'none',
                    minHeight: '48px',
                    maxHeight: '120px',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b2020';
                    e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  style={{
                    padding: '12px 20px',
                    background: (inputMessage.trim() && !isLoading) ? '#8b2020' : 'rgba(139, 32, 32, 0.3)',
                    color: (inputMessage.trim() && !isLoading) ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (inputMessage.trim() && !isLoading) ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    height: '48px'
                  }}
                  onMouseEnter={(e) => {
                    if (inputMessage.trim() && !isLoading) {
                      e.currentTarget.style.background = '#6b1818';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (inputMessage.trim() && !isLoading) {
                      e.currentTarget.style.background = '#8b2020';
                    }
                  }}
                >
                  {isLoading ? (
                    <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Send size={18} />
                  )}
                  Send
                </button>
              </div>

              <button
                onClick={handleDisconnect}
                style={{
                  padding: '8px 16px',
                  background: 'transparent',
                  color: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                }}
              >
                Disconnect & Change Agent
              </button>
            </div>
          </>
        )}
      </div>

      {/* Add spinning animation for loading */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}

export default AgentChat;

